import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { StepTitle, OField, inputCls, NavButtons, ELD_PROVIDERS } from "@/components/onboard/ui";
import { Minus, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

function Counter({ label, value, onChange }) {
  const v = Number(value || 0);
  return (
    <div className={`rounded-2xl border p-4 transition-colors ${v > 0 ? "border-sky-400/50 bg-sky-400/[0.07]" : "border-white/10 bg-white/[0.02]"}`}>
      <p className="text-sm font-semibold text-white">{label}</p>
      <div className="mt-3 flex items-center justify-between">
        <button type="button" onClick={() => onChange(Math.max(0, v - 1))} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 grid place-items-center text-white/70 hover:text-white" data-testid={`ob-count-minus-${label}`}><Minus size={14} /></button>
        <span className="font-mono font-bold text-2xl text-white" data-testid={`ob-count-${label}`}>{v}</span>
        <button type="button" onClick={() => onChange(v + 1)} className="w-9 h-9 rounded-full bg-white/10 border border-white/15 grid place-items-center text-white hover:bg-sky-500" data-testid={`ob-count-plus-${label}`}><Plus size={14} /></button>
      </div>
    </div>
  );
}

export function StepCompany({ token, state, onSaved, onBack }) {
  const prev = state.wizard.company || {};
  const fm = state.wizard.fmcsa || {};
  const { truck_types, trailer_sizes } = state.options;
  const [f, setF] = useState({
    legal_name: prev.legal_name || fm.legal_name || "", address: prev.address || fm.address || "", ein: prev.ein || "",
    truck_counts: prev.truck_counts || {}, trailer_sizes: prev.trailer_sizes || [], max_weight: prev.max_weight || "",
    eld: prev.eld || "", eld_other: prev.eld_other || "", factoring: prev.factoring || "",
  });
  const [loading, setLoading] = useState(false);
  const total = Object.values(f.truck_counts).reduce((s, n) => s + Number(n || 0), 0);
  const toggleSize = (s) => setF({ ...f, trailer_sizes: f.trailer_sizes.includes(s) ? f.trailer_sizes.filter((x) => x !== s) : [...f.trailer_sizes, s] });

  const save = async () => {
    setLoading(true);
    try {
      const eld = f.eld === "Other" ? f.eld_other || "Other" : f.eld;
      const { data } = await api.post(`/onboard/${token}/save`, { step: "company", data: { ...f, eld } });
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <StepTitle kicker="Step 3 · Company Profile" title="Tell Us About Your Fleet" sub="This shapes your plan, your documents and your agreement, so please be accurate." />
      <div className="grid sm:grid-cols-2 gap-4">
        <OField label="Company Legal Name *"><input value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} className={inputCls} data-testid="ob-legal-name" /></OField>
        <OField label="Address *"><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} data-testid="ob-address" /></OField>
        <OField label="EIN (Federal Tax ID)"><input value={f.ein} onChange={(e) => setF({ ...f, ein: e.target.value })} placeholder="12-3456789" className={`${inputCls} font-mono`} data-testid="ob-ein" /></OField>
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50 mt-8 mb-3">Number of Trucks by Type * <span className="text-sky-300 normal-case tracking-normal ml-2" data-testid="ob-total-trucks">Total: {total}</span></p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {truck_types.map((t) => <Counter key={t} label={t} value={f.truck_counts[t]} onChange={(n) => setF({ ...f, truck_counts: { ...f.truck_counts, [t]: n } })} />)}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50 mt-8 mb-3">Trailer Size</p>
      <div className="flex flex-wrap gap-2">
        {trailer_sizes.map((s) => (
          <button key={s} type="button" onClick={() => toggleSize(s)} data-testid={`ob-trailer-${s}`} className={`h-10 px-5 rounded-full border text-sm font-semibold transition-colors ${f.trailer_sizes.includes(s) ? "bg-white text-slate-900 border-white" : "border-white/15 text-white/70 hover:border-white/40"}`}>{s}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        <OField label="Max Weight Capacity (lbs)"><input type="number" value={f.max_weight} onChange={(e) => setF({ ...f, max_weight: e.target.value })} placeholder="45000" className={`${inputCls} font-mono`} data-testid="ob-max-weight" /></OField>
        <OField label="ELD Company">
          <select value={f.eld} onChange={(e) => setF({ ...f, eld: e.target.value })} className={`${inputCls} [&>option]:text-slate-900`} data-testid="ob-eld">
            <option value="">Select ELD…</option>{ELD_PROVIDERS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </OField>
        <OField label="Factoring Company"><input value={f.factoring} onChange={(e) => setF({ ...f, factoring: e.target.value })} placeholder="e.g. RTS Financial or None" className={inputCls} data-testid="ob-factoring" /></OField>
        {f.eld === "Other" && <OField label="ELD Company Name" className="sm:col-span-3"><input value={f.eld_other} onChange={(e) => setF({ ...f, eld_other: e.target.value })} className={inputCls} data-testid="ob-eld-other" /></OField>}
      </div>
      <NavButtons onBack={onBack} onNext={save} loading={loading} disabled={total === 0} nextLabel="Save & Continue" />
    </div>
  );
}
